// src/pages/user/CompanyQuotePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FileText, Download, Calendar, Clock, CheckCircle, XCircle,
  Send, AlertCircle, Eye, RefreshCw, ArrowRight,
} from "lucide-react";
import { useEditorLink } from "../../utils/useEditorLink";
import { injectUserStyles, statusBadgeClass, fmtStatus } from "./userStyles";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const STYLE_ID = "cqp-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .cqp-table-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; overflow:hidden; }
    .cqp-table { width:100%; border-collapse:collapse; font-size:13px; }
    .cqp-table thead tr { background:#f9fafb; border-bottom:1px solid #e8ecf0; }
    .cqp-table th { padding:10px 16px; text-align:left; font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.04em; color:#9ca3af; white-space:nowrap; }
    .cqp-table td { padding:13px 16px; border-bottom:1px solid #f3f4f6; vertical-align:middle; }
    .cqp-table tbody tr:last-child td { border-bottom:none; }
    .cqp-table tbody tr:hover td { background:#fafafa; }
    .cqp-proj-name  { font-size:13.5px; font-weight:500; color:#0c1118; }
    .cqp-date-main  { font-size:12.5px; color:#374151; display:flex; align-items:center; gap:5px; }
    .cqp-date-sub   { font-family:'DM Sans',sans-serif; font-size:10px; color:#d1d5db; margin-top:2px; }
    .cqp-download-link {
      display:inline-flex; align-items:center; gap:5px;
      font-size:12.5px; font-weight:500; color:#0c1118; text-decoration:none;
      padding:5px 10px; border:1px solid #e2e8f0; border-radius:6px;
      transition:background 150ms, border-color 150ms;
    }
    .cqp-download-link:hover { background:#f3f4f6; border-color:#d1d5db; }
    .cqp-not-ready { font-size:12px; color:#d1d5db; font-style:italic; }
    .cqp-view-btn {
      display:inline-flex; align-items:center; gap:5px;
      padding:5px 11px; border:1px solid #e2e8f0; border-radius:6px;
      font-size:12.5px; color:#374151; cursor:pointer; background:#fff;
      transition:background 150ms; font-family:'DM Sans',sans-serif;
    }
    .cqp-view-btn:hover { background:#f3f4f6; }
  `;
  document.head.appendChild(el);
}

async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, { headers: { "Content-Type": "application/json" }, credentials: "include", ...opts });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export default function CompanyQuotePage() {
  useEffect(() => { injectLocal(); }, []);

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const { getEditorUrl } = useEditorLink();

  async function load() {
    setLoading(true);
    try { const r = await jfetch("/api/quotes/my"); setItems(r.items || []); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    total:   items.length,
    sent:    items.filter(q => q.status === "sent_to_client").length,
    pending: items.filter(q => q.status === "pending_review").length,
  }), [items]);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:"12px", marginBottom:"24px" }}>
        <div>
          <div className="up-title">My Quote Requests</div>
          <div className="up-sub">Requests you've submitted to your company — quotes appear here when staff upload them</div>
        </div>
        <button className="up-btn up-btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={14} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} /> Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="up-kpi-grid" style={{ gridTemplateColumns:"repeat(3,1fr)", marginBottom:"20px" }}>
        {[
          { accent:"#0c1118", val: totals.total,   lbl:"Total Requests"   },
          { accent:"#f9ab14", val: totals.pending, lbl:"Pending Review"   },
          { accent:"#6366f1", val: totals.sent,    lbl:"Sent to Me"       },
        ].map(k => (
          <div className="up-kpi" key={k.lbl}>
            <div className="up-kpi-accent" style={{ background: k.accent }} />
            <div className="up-kpi-val">{loading ? "—" : k.val}</div>
            <div className="up-kpi-lbl">{k.lbl}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="cqp-table-card">
        {loading ? (
          <div className="up-loading"><div className="up-spinner" /> Loading…</div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table className="cqp-table">
              <thead><tr>
                <th>Project</th>
                <th>Requested</th>
                <th>Status</th>
                <th>Quote File</th>
                <th style={{ textAlign:"right" }}>Action</th>
              </tr></thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={5}>
                    <div className="up-empty">
                      <FileText size={36} style={{ color:"#d1d5db", margin:"0 auto" }} />
                      <div className="up-empty-title">No quote requests yet</div>
                      <div className="up-empty-sub">Submit a quote request from any project to get started</div>
                    </div>
                  </td></tr>
                ) : items.map(q => (
                  <tr key={q.id}>
                    <td><div className="cqp-proj-name">{q.project?.name || q.projectId}</div></td>
                    <td>
                      <div className="cqp-date-main"><Calendar size={12} style={{ color:"#9ca3af" }} />{new Date(q.createdAt).toLocaleDateString()}</div>
                      <div className="cqp-date-sub">{new Date(q.createdAt).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}</div>
                    </td>
                    <td><span className={statusBadgeClass(q.status)}>{fmtStatus(q.status)}</span></td>
                    <td>
                      {q.fileUrl
                        ? <a href={`${BASE}/api/quotes/${q.id}/file`} className="cqp-download-link" target="_blank" rel="noreferrer"><Download size={13} /> Download</a>
                        : <span className="cqp-not-ready">Not uploaded yet</span>}
                    </td>
                    <td style={{ textAlign:"right" }}>
                      <button className="cqp-view-btn" onClick={() => window.open(getEditorUrl(q.projectId), "_blank")}>
                        <Eye size={13} /> View Project
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
